import { _ } from "../../../../../core/polyglot/polyglot.js";
import {
	getDataStyleForDepth,
	getSummaryStyleForDepth,
} from "./excelStyles.js";
import { makeEmptyCell, makeSummaryTitleCellForRow } from "./helper.js";
import RowRenderer from "../../../RowRenderer/RowRenderer.js";

/**
 * Adapt {@link RowRenderer} for a xlsx-js-style Excel export.
 * In particular, an Excel export needs a blank line after every summary row.
 *
 * @param {function} [setRowLevel] - Function invoked with a row and its level
 *   so that the visitor can generate row metadata out of it
 */
export default class ExcelRowRenderer extends RowRenderer {
	constructor({ setRowLevel = () => {} } = {}) {
		super(...arguments);
		this._currentLevel = 0;
		this._setRowLevel = setRowLevel;
	}

	visitAbstractRow(row) {
		return [
			[this._rowTitleRenderer(row), ...this._dataCellsRenderer({ row })],
		];
	}

	visitAll(rows) {
		const result = [];

		rows.forEach((row) => {
			result.push(...this.visit(row));
		});

		return result;
	}

	visitAccountRow(accountRow) {
		return this.visitAbstractRow(accountRow);
	}

	visitSpacerRow(row) {
		return [[makeEmptyCell(), ...this._emptyDataColumnsRenderer({ row })]];
	}

	visitStaticReportRow(row) {
		return this.visitAbstractRow(row);
	}

	visitGroupRow(groupRow) {
		// Override to render a blank line after the summary row of a
		// group row
		const renderer = super.visitGroupRow(groupRow);

		if (!this._report.hasSummaryRow({ groupRow })) {
			return renderer;
		}

		return [...renderer, ...this._blankLineRenderer({ row: groupRow })];
	}

	visitTextRow(row) {
		return [
			[
				this._rowTitleRenderer(row),
				...this._emptyDataColumnsRenderer({ row }),
			],
		];
	}

	_dataCellsRenderer({ row }) {
		this._updateCurrentLevel({ row });

		return super._dataCellsRenderer(...arguments);
	}

	_getCellData({ row, column }) {
		return row.getReport().getData({ row, column });
	}

	_emptyDataColumnsRenderer({ row }) {
		this._updateCurrentLevel({ row });
		return super._emptyDataColumnsRenderer(...arguments);
	}

	_collapsedGroupRowRenderer(group) {
		const visibleLeafRows = this._getGroupRowVisibleLeaves(group);

		return [
			...this.visitAbstractRow(group),
			...this.visitAll(visibleLeafRows),
		];
	}

	_firstRowOfGroupRowRenderer({ groupRow, dataCells }) {
		return [this._rowTitleRenderer(groupRow), ...dataCells];
	}

	_getDepthCssClass(row) {
		return getDataStyleForDepth({ row });
	}

	_blankLineRenderer({ row }) {
		return [[makeEmptyCell(), ...this._emptyDataColumnsRenderer({ row })]];
	}

	_summaryRowOfGroupRowRenderer(groupRow) {
		const content = _("Sum {title}", { title: groupRow.getTitle() });

		return [
			makeSummaryTitleCellForRow({
				row: groupRow,
				content,
			}),
			...this._dataCellsRenderer({
				row: groupRow,
				cssClass: getSummaryStyleForDepth({
					row: groupRow,
				}),
			}),
		];
	}

	_updateCurrentLevel({ row }) {
		const newLevel = row.getVisibilityDepth?.();

		if (newLevel !== undefined) {
			this._currentLevel = newLevel - 1;
		}

		this._setRowLevel({ row, level: this._currentLevel });
	}
}
